import { useTranslation } from "react-i18next";
import { Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useEffect } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { OrganizationAdminDto, UserAccountDto } from "../api/client-axios";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { clearErrorOrganizationAdmins, deleteOrganizationAdmin, fetchOrganizationsAdmins, getOrganization, selectOrganizationAdmins, startDeleteOrganizationAdmins } from "../features/organizationAdminsSlice";
import { RouteNames } from "../routes";
import { CrudModals } from "../components/CrudModals";
import { CrudToolbar } from "../components/CrudToolbar";
import { EmptyState } from "../components/EmptyState";
import { TableSkeleton } from "../components/TableSkeleton";

export const OrganizationAdminsPage = (
    props: {
        organizationId: string
    }
) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const organization = useAppSelector(state => state.organizationAdmins.organization);
    const organizationAdmins = useAppSelector(state => state.organizationAdmins.organizationAdmins);
    const status = useAppSelector(state => state.organizationAdmins.status);
    const selectedOrganizationAdmin = useAppSelector(state => state.organizationAdmins.selectedOrganizationAdmin);
    const error = useAppSelector(state => state.organizationAdmins.error);
    const params = useParams();
    const navigate = useNavigate();

    useEffect(() => {
            dispatch(getOrganization(props.organizationId));
    }, []);

    useEffect(() => {
        if(organization != null) {
            dispatch(fetchOrganizationsAdmins(organization.id!));
        }
    },[ organization]);

    if(params.adminId !== undefined) {
        return <Outlet />
    }

    const onWarnDeleteOrganizationAdmin = () => {
        if (selectedOrganizationAdmin != null) {
            dispatch(startDeleteOrganizationAdmins());
        }
    }

    const onAddOrganizationAdmin = () => {
        navigate(`/${RouteNames.organizations}/${organization?.id}/admins/new`);
    }

    const onEditOrganizationAdmin = () => {
        if (selectedOrganizationAdmin != null) {
           navigate(`/${RouteNames.organizations}/${organization?.id}/admins/${selectedOrganizationAdmin.id}`);
        }
    }

    const onReload = () => {
        if (organization != null) {
            dispatch(fetchOrganizationsAdmins(organization.id!));
        }
    }

    const onRow = (organization: OrganizationAdminDto, index: number | undefined) => {
        return {
            onClick: () => {
                dispatch(selectOrganizationAdmins(organization))
            }, // click row
        }
    }

    const onDeleteOrganizationAdmin = () => {
        if (selectedOrganizationAdmin != null && organization != null) {
            dispatch(deleteOrganizationAdmin({
                organizationId: organization.id!,
                id: selectedOrganizationAdmin.id!
            }));
        }
    }

    const columns: ColumnsType<OrganizationAdminDto> = [
        {

            // width: '100%',
            title: t("column.firstName"),
            dataIndex: 'firstName',
            key: 'firstName',
            sorter: (a, b) => a.firstName! > b.firstName! ? -1 : 1,
        },
        {

            //width: '100%',
            title: t("column.lastName"),
            dataIndex: 'lastName',
            key: 'lastName',
            sorter: (a, b) => a.firstName! > b.firstName! ? -1 : 1,
        },
        {

            //width: '100%',
            title: t("column.email"),
            dataIndex: 'userAccount',
            key: 'lastName',
            sorter: (a, b) => a.userAccount?.emailAddress! > b.userAccount?.emailAddress! ? -1 : 1,
            render: (userAccount?: UserAccountDto) => userAccount?.emailAddress
        },

    ];

    const getRowClassName = (organization: OrganizationAdminDto, index: number) => {
        return selectedOrganizationAdmin != null && selectedOrganizationAdmin.id === organization.id ? 'bg-selected' : '';
    }

    const content = status === 'loading' || status === 'idle'
        ? <TableSkeleton />
        : <>
            <CrudToolbar
                hasSelection={selectedOrganizationAdmin != null}
                canEdit={selectedOrganizationAdmin?.id != null}
                selectionLabel={[selectedOrganizationAdmin?.firstName, selectedOrganizationAdmin?.lastName].filter(Boolean).join(" ")}
                onDelete={onWarnDeleteOrganizationAdmin}
                onEdit={onEditOrganizationAdmin}
                onAdd={onAddOrganizationAdmin}
                onReload={onReload}
                onClearSelection={() => dispatch(selectOrganizationAdmins(undefined))}
            />
            <div className="card card--flush">
                <Table
                    rowKey={(organizationAdmin: OrganizationAdminDto) => organizationAdmin.id!}
                    rowClassName={getRowClassName}
                    onRow={onRow}
                    dataSource={organizationAdmins}
                    columns={columns}
                    locale={{ emptyText: <EmptyState /> }}
                    pagination={{ hideOnSinglePage: true, defaultPageSize: 12, showSizeChanger: false }}
                />
            </div>

            <CrudModals
                status={status}
                error={error}
                entityKey="organizationAdmin"
                deleteTarget={[selectedOrganizationAdmin?.firstName, selectedOrganizationAdmin?.lastName].filter(Boolean).join(" ")}
                onDismiss={() => dispatch(clearErrorOrganizationAdmins())}
                onConfirmDelete={onDeleteOrganizationAdmin}
            />
        </>

    return content;
}
